import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    Form, Modal, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate, formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function InventaireDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inventaire, setInventaire] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    useEffect(() => {
        fetchInventaire();
    }, [id]);

    const fetchInventaire = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setInventaire(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleValider = async () => {
        setIsValidating(true);
        
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            const response = await fetch(`${API_BASE_URL}/inventaires.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'valider',
                    id: id,
                    id_utilisateur: user?.id || user?.id_utilisateur || 1
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowValidateModal(false);
                fetchInventaire();
            } else {
                alert(result.message || "Erreur lors de la validation");
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
        } finally {
            setIsValidating(false);
        }
    };

    const handleAnnuler = async () => {
        setIsCancelling(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch(`${API_BASE_URL}/inventaires.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'annuler',
                    id: id
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowCancelModal(false);
                fetchInventaire();
            } else {
                alert(result.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
        } finally {
            setIsCancelling(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#f59e0b', icon: 'ri-time-line', label: 'Brouillon' },
            valide: { color: '#10b981', icon: 'ri-check-double-line', label: 'Validé' },
            annule: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const calculerTauxPrecision = () => {
        if (!inventaire?.lignes) return 0;
        const produitsSansEcart = inventaire.lignes.filter(l => l.ecart === 0).length;
        return Math.round((produitsSansEcart / inventaire.lignes.length) * 100) || 0;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de l'inventaire...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !inventaire) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Inventaire non trouvé"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/stock/inventaire')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const tauxPrecision = calculerTauxPrecision();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <Button 
                            variant="link" 
                            className="p-0 text-muted text-decoration-none" 
                            onClick={() => navigate('/stock/inventaire')}
                        >
                            <i className="ri-arrow-left-line me-1"></i>Inventaires
                        </Button>
                        <i className="ri-arrow-right-s-line text-muted"></i>
                        <span className="text-primary">Détail inventaire</span>
                    </div>

                    {/* En-tête moderne avec carte profil */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                        <div className="profile-header" style={{ 
                            height: '120px', 
                            background: 'linear-gradient(135deg, #21ACCD, #1a8ba8)',
                            position: 'relative'
                        }}>
                            <div className="profile-bg-shape"></div>
                        </div>
                        <Card.Body className="position-relative" style={{ marginTop: '-60px' }}>
                            <Row>
                                <Col md={8}>
                                    <div className="d-flex align-items-end gap-4 flex-wrap">
                                        <div className="profile-avatar">
                                            <div className="avatar-circle">
                                                <i className="ri-stack-line fs-24"></i>
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <h1 className="display-6 fw-bold mb-1" style={{ color: '#fff' }}>
                                                {inventaire.reference_inventaire}
                                            </h1>
                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <div className="d-flex align-items-center gap-1">
                                                    <i className="ri-calendar-line text-muted"></i>
                                                    <span className="text-muted small">{formatDate(inventaire.date_inventaire)}</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <i className="ri-user-line text-muted"></i>
                                                    <span className="text-muted small">{inventaire.prenom_utilisateur} {inventaire.nom_utilisateur}</span>
                                                </div>
                                                {getStatutBadge(inventaire.statut)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex justify-content-md-end gap-2 mt-3 mt-md-0">
                                        <Button 
                                            variant="secondary" 
                                            onClick={() => navigate('/stock/inventaire')}
                                            className="rounded-pill px-3 d-flex align-items-center gap-1"
                                            size="sm"
                                        >
                                            <i className="ri-arrow-left-line"></i>
                                            Retour
                                        </Button>
                                        {inventaire.statut === 'brouillon' && (
                                            <>
                                                <Button 
                                                    variant="success" 
                                                    onClick={() => setShowValidateModal(true)}
                                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                                    size="sm"
                                                    style={{ background: '#10b981', border: 'none' }}
                                                >
                                                    <i className="ri-check-double-line"></i>
                                                    Valider
                                                </Button>
                                                <Button 
                                                    variant="danger" 
                                                    onClick={() => setShowCancelModal(true)}
                                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                                    size="sm"
                                                >
                                                    <i className="ri-close-circle-line"></i>
                                                    Annuler
                                                </Button>
                                            </>
                                        )}
                                        {inventaire.statut === 'valide' && (
                                            <span className="status-badge align-self-center bg-success" style={{ color: '#fff' }}>
                                                <i className="ri-check-double-line me-1 fs-10"></i>
                                                Inventaire validé
                                            </span>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-stack-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Produits comptés</span>
                                    <h3 className="kpi-value">{inventaire.lignes?.length || 0}</h3>
                                    <span className="kpi-unit">articles</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className={`kpi-card ${inventaire.total_ecart > 0 ? 'kpi-success' : inventaire.total_ecart < 0 ? 'kpi-danger' : 'kpi-secondary'}`}>
                                <div className="kpi-icon">
                                    <i className={`ri-${inventaire.total_ecart > 0 ? 'arrow-up' : inventaire.total_ecart < 0 ? 'arrow-down' : 'equal'}-line fs-24`}></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Écart total</span>
                                    <h3 className="kpi-value">{inventaire.total_ecart > 0 ? '+' : ''}{inventaire.total_ecart || 0}</h3>
                                    <span className="kpi-unit">unités</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Valeur écart</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(inventaire.valeur_ecart || 0)}</h3>
                                    <span className="kpi-unit">FCFA</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-percent-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Précision</span>
                                    <h3 className="kpi-value">{tauxPrecision}%</h3>
                                    <div className="progress-bar-custom mt-1" style={{ width: '100%' }}>
                                        <div className="progress-fill" style={{ width: `${tauxPrecision}%`, background: '#0ea5e9' }}></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Commentaire */}
                    {inventaire.commentaire && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex gap-3">
                                    <div className="comment-icon">
                                        <i className="ri-chat-1-line text-primary"></i>
                                    </div>
                                    <div>
                                        <h6 className="mb-2 fw-semibold">Commentaire</h6>
                                        <p className="mb-0 text-muted">{inventaire.commentaire}</p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Détail des lignes */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-list-check-2 text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Détail de l'inventaire</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                    {inventaire.lignes?.length || 0} produits
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Produit</th>
                                            <th>Catégorie</th>
                                            <th className="text-center">Théorique</th>
                                            <th className="text-center">Réel</th>
                                            <th className="text-center">Écart</th>
                                            <th className="text-end pe-4">Valeur écart</th>
                                            <th>Commentaire</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventaire.lignes?.map((ligne, index) => (
                                            <tr key={ligne.id_ligne_inventaire} className="inventaire-row" style={{
                                                backgroundColor: ligne.ecart > 0 ? '#ecfdf5' : ligne.ecart < 0 ? '#fef2f2' : ''
                                            }}>
                                                <td className="ps-4">
                                                    <span className="reference-badge">{ligne.reference}</span>
                                                </td>
                                                <td>
                                                    <Link to={`/stock/produits/${ligne.id_produit}`} className="table-link">
                                                        {ligne.designation}
                                                    </Link>
                                                </td>
                                                <td>{ligne.nom_categorie || <span className="text-muted">-</span>}</td>
                                                <td className="text-center fw-bold">{ligne.quantite_theorique}</td>
                                                <td className="text-center fw-bold">{ligne.quantite_reelle}</td>
                                                <td className="text-center">
                                                    {ligne.ecart !== 0 && (
                                                        <span className="status-badge" style={{ background: ligne.ecart > 0 ? '#10b98115' : '#ef444415', color: ligne.ecart > 0 ? '#10b981' : '#ef4444' }}>
                                                            <i className={`ri-${ligne.ecart > 0 ? 'arrow-up' : 'arrow-down'}-line me-1 fs-10`}></i>
                                                            {ligne.ecart > 0 ? '+' : ''}{ligne.ecart}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-end pe-4">
    {(() => {
        const ecart = ligne.ecart || 0;
        const prix = ligne.prix_achat_ht || 0;
        const valeurEcart = ecart * prix;
        return (
            <span className={`fw-semibold ${valeurEcart !== 0 ? 'text-primary' : 'text-muted'}`}>
                {formatCurrency(valeurEcart)}
            </span>
        );
    })()}
</td>
                                                <td>
                                                    {ligne.commentaire ? (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>{ligne.commentaire}</Tooltip>}>
                                                            <span className="comment-preview">
                                                                <i className="ri-chat-1-line me-1 text-muted"></i>
                                                                {ligne.commentaire.length > 30 ? ligne.commentaire.substring(0, 30) + '...' : ligne.commentaire}
                                                            </span>
                                                        </OverlayTrigger>
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de validation modernisé */}
                <Modal show={showValidateModal} onHide={() => setShowValidateModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#10b981' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-check-double-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Valider l'inventaire</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4">
                        <p className="text-center text-muted mb-4">
                            Êtes-vous sûr de vouloir valider l'inventaire <strong className="text-primary">{inventaire?.reference_inventaire}</strong> ?
                        </p>
                        <Alert variant="warning" className="rounded-3">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Attention !</strong>
                                    <p className="mb-0 small mt-1">
                                        La validation appliquera les écarts au stock en temps réel.
                                        Cette action est irréversible.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                        <div className="bg-light p-4 rounded-3 mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">Produits avec écart :</span>
                                <Badge className="rounded-pill px-3" style={{ background: '#f59e0b' }}>
                                    {inventaire?.lignes?.filter(l => l.ecart !== 0).length}
                                </Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">Écart total :</span>
                                <span className={`fw-bold fs-5 ${inventaire?.total_ecart > 0 ? 'text-success' : inventaire?.total_ecart < 0 ? 'text-danger' : ''}`}>
                                    {inventaire?.total_ecart > 0 ? '+' : ''}{inventaire?.total_ecart || 0}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Valeur totale des écarts :</span>
                                <span className="fw-bold fs-5 text-primary">{formatCurrency(inventaire?.valeur_ecart || 0)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowValidateModal(false)} className="rounded-pill px-4" disabled={isValidating}>
                            Annuler
                        </Button>
                        <Button variant="success" onClick={handleValider} className="rounded-pill px-5" disabled={isValidating} style={{ background: '#10b981', border: 'none' }}>
                            {isValidating ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-double-line me-2"></i>}
                            Confirmer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal d'annulation modernisé */}
                <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#ef4444' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-close-circle-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Annuler l'inventaire</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir annuler l'inventaire <strong className="text-primary">{inventaire?.reference_inventaire}</strong> ?
                        </p>
                        <Alert variant="danger" className="rounded-3">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">L'annulation supprimera définitivement cet inventaire.</p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)} className="rounded-pill px-4" disabled={isCancelling}>
                            Non, conserver
                        </Button>
                        <Button variant="danger" onClick={handleAnnuler} className="rounded-pill px-5" disabled={isCancelling}>
                            {isCancelling ? <Spinner size="sm" className="me-2" /> : <i className="ri-close-circle-line me-2"></i>}
                            Oui, annuler
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* Profile Header */
                .profile-header {
                    position: relative;
                    overflow: hidden;
                }
                .profile-bg-shape {
                    position: absolute;
                    top: -20px;
                    right: 20px;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                    pointer-events: none;
                }
                .profile-avatar {
                    margin-bottom: -10px;
                }
                .avatar-circle {
                    width: 100px;
                    height: 100px;
                    background: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
                }
                .avatar-circle i {
                    font-size: 48px;
                    color: #21ACCD;
                }
                
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-secondary { border-left-color: #64748b; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-secondary .kpi-icon { background: #f1f5f9; color: #64748b; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Comment Preview */
                .comment-preview {
                    cursor: pointer;
                    display: inline-block;
                    max-width: 150px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #475569;
                }
                .comment-preview:hover {
                    color: #21ACCD;
                }
                
                .comment-icon {
                    width: 40px;
                    height: 40px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .inventaire-row {
                    transition: all 0.2s ease;
                }
                
                .inventaire-row:hover {
                    background: #f8fafc !important;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}